import { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, Users, Database, Target, ArrowUpRight, ArrowDownRight, AlertTriangle, Banknote } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#0F172A', '#2563EB', '#7C3AED', '#10B981', '#F59E0B'];

const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', PKR: 'Rs', INR: '₹', AED: 'AED',
  JPY: '¥', CNY: '¥', CAD: 'C$', AUD: 'A$', SAR: 'SAR', BRL: 'R$',
};

const formatCurrency = (amount, currency) => {
  const sym = CURRENCY_SYMBOLS[currency] || currency + ' ';
  return `${sym}${amount.toLocaleString()}`;
};

const CustomTooltip = ({ active, payload, label, currency }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="text-sm font-medium text-slate-700">{label}</p>
        <p className="text-xs text-slate-600 mt-1">
          {payload[0].name}: <span className="font-mono font-semibold">{formatCurrency(payload[0].value, currency)}</span>
        </p>
      </div>
    );
  }
  return null;
};

export const Dashboard = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/analytics/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-500">Loading dashboard...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Database className="mx-auto mb-4 text-slate-300" size={48} />
          <p className="text-slate-600">No data available. Upload your spend data to get started.</p>
        </div>
      </div>
    );
  }

  const currency = stats.currency || 'USD';

  const statCards = [
    {
      label: 'Total Spend',
      value: formatCurrency(stats.total_spend, currency),
      icon: TrendingUp,
      color: 'text-[#2563EB]',
      bg: 'bg-blue-50'
    },
    {
      label: 'Total Suppliers',
      value: stats.total_suppliers.toLocaleString(),
      icon: Users,
      color: 'text-[#7C3AED]',
      bg: 'bg-purple-50'
    },
    {
      label: 'Spend Records',
      value: stats.total_records.toLocaleString(),
      icon: Database,
      color: 'text-[#10B981]',
      bg: 'bg-green-50'
    },
    {
      label: 'Data Quality',
      value: `${stats.data_quality_score.toFixed(1)}%`,
      icon: Target,
      color: 'text-[#F59E0B]',
      bg: 'bg-orange-50'
    }
  ];

  const displayName = stats.organization_name || stats.full_name || '';

  return (
    <div className="space-y-6">
      <div>
        {displayName && (
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold uppercase tracking-widest text-blue-600 bg-blue-50 px-2.5 py-1 rounded-sm" data-testid="dashboard-org-name">
              {displayName}
            </span>
          </div>
        )}
        <h1 className="text-3xl font-heading font-bold text-[#0F172A] mb-2">Dashboard Overview</h1>
        <p className="text-slate-600">Your procurement intelligence at a glance</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              data-testid={`stat-card-${stat.label.toLowerCase().replace(' ', '-')}`}
              className="stat-card bg-white border border-slate-200 rounded-sm p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`${stat.bg} p-3 rounded-sm`}>
                  <Icon className={stat.color} size={24} />
                </div>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">{stat.label}</p>
                <p className="text-2xl font-heading font-bold text-[#0F172A]">{stat.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-lg font-heading font-semibold text-[#0F172A] mb-4">Spend by Category</h2>
          {stats.top_categories && stats.top_categories.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stats.top_categories}
                  dataKey="spend"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(entry) => `${entry.category} (${entry.percentage.toFixed(1)}%)`}
                >
                  {stats.top_categories.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip currency={currency} />} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-slate-400">
              <p>No category data available</p>
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-lg font-heading font-semibold text-[#0F172A] mb-4">Recent Uploads</h2>
          {stats.recent_uploads && stats.recent_uploads.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_uploads.map((upload, index) => (
                <div
                  key={upload.id}
                  data-testid={`recent-upload-${index}`}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-sm border border-slate-100"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-700">{upload.filename}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {upload.records} records • {upload.status}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block px-2 py-1 text-xs rounded-sm ${
                      upload.status === 'classified' ? 'bg-green-100 text-green-700' :
                      upload.status === 'cleansed' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {upload.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-slate-400">
              <p>No uploads yet</p>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-lg font-heading font-semibold text-[#0F172A] mb-1">Classification Accuracy</h2>
          <p className="text-3xl font-heading font-bold text-[#2563EB] mt-2">
            {stats.classification_accuracy.toFixed(1)}%
          </p>
          <p className="text-sm text-slate-500 mt-2">Average AI classification confidence</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-lg font-heading font-semibold text-[#0F172A] mb-1">Data Quality Score</h2>
          <p className="text-3xl font-heading font-bold text-[#10B981] mt-2">
            {stats.data_quality_score.toFixed(1)}%
          </p>
          <p className="text-sm text-slate-500 mt-2">Overall data cleansing quality</p>
        </div>
      </div>

      {/* Top Suppliers + Cash Risk */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="top-suppliers-chart">
          <h2 className="text-lg font-heading font-semibold text-[#0F172A] mb-4">Top Suppliers by Spend</h2>
          {stats.top_suppliers && stats.top_suppliers.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.top_suppliers} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tickFormatter={(v) => formatCurrency(v, currency)} tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="supplier" width={120} tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip currency={currency} />} />
                <Bar dataKey="spend" name="Spend" fill="#2563EB" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-slate-400">
              <p>No supplier data available</p>
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="cash-risk-panel">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="text-red-500" size={20} />
            <h2 className="text-lg font-heading font-semibold text-[#0F172A]">Cash Transaction Risk</h2>
          </div>
          {stats.cash_risk_summary && stats.cash_risk_summary.total_cash_transactions > 0 ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-red-50 rounded-sm p-4">
                  <p className="text-xs text-red-600 font-semibold uppercase">Cash Spend</p>
                  <p className="text-xl font-bold text-red-700 mt-1">{formatCurrency(stats.cash_risk_summary.total_cash_spend, currency)}</p>
                  <p className="text-xs text-red-500 mt-0.5">{stats.cash_risk_summary.cash_spend_percentage.toFixed(1)}% of total</p>
                </div>
                <div className="bg-amber-50 rounded-sm p-4">
                  <p className="text-xs text-amber-600 font-semibold uppercase">Cash Transactions</p>
                  <p className="text-xl font-bold text-amber-700 mt-1">{stats.cash_risk_summary.total_cash_transactions.toLocaleString()}</p>
                  <p className="text-xs text-amber-500 mt-0.5">{stats.cash_risk_summary.cash_suppliers} suppliers</p>
                </div>
              </div>
              {stats.cash_risk_summary.top_cash_suppliers && stats.cash_risk_summary.top_cash_suppliers.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Top Cash Suppliers</p>
                  <div className="space-y-2">
                    {stats.cash_risk_summary.top_cash_suppliers.map((s, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                        <span className="text-sm text-slate-700 truncate max-w-[180px]">{s.supplier}</span>
                        <div className="text-right">
                          <span className="text-sm font-semibold text-red-600">{formatCurrency(s.cash_spend, currency)}</span>
                          <span className="text-xs text-slate-400 ml-2">({s.cash_transactions} txns)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-slate-400">
              <Banknote size={32} className="mb-2 text-green-300" />
              <p className="text-sm">No cash transactions detected</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};